import type { HelmUser, HelmUserRole, Profile } from '../../lib/api/types'

/** The roles that make a login Xtellar staff rather than a customer. */
export const STAFF_ROLES: readonly HelmUserRole[] = [
  'admin',
  'accounts',
  'viewer',
  'warehouse',
  'forwarder',
]

/** A customer's own login: not an admin, and holding none of the staff roles. */
export function isCustomerLogin(
  profile: Pick<Profile, 'role' | 'is_admin' | 'helm_roles'> | null | undefined,
) {
  if (!profile || profile.is_admin || profile.role === 'admin') return false
  return !(profile.helm_roles ?? []).some((role) => STAFF_ROLES.includes(role))
}

/**
 * Xtellar's team, as Beacon's list_team reads it: admins and the staff roles.
 * Who an owner or a task's assignee can be.
 */
export function isTeamMember(user: Pick<HelmUser, 'profile_role' | 'helm_roles'>) {
  return (
    user.profile_role === 'admin' ||
    (user.helm_roles ?? []).some((assignment) => STAFF_ROLES.includes(assignment.role))
  )
}

/** What a login may open in Helm. The menu and the guided tours both follow it. */
export interface HelmAccess {
  admin: boolean
  customer: boolean
  /** The warehouse team sees the warehouse and nothing else. */
  warehouseOnly: boolean
  /** The Warehouse page: the warehouse team and admins. */
  seesWarehouse: boolean
}

export function helmAccess(
  profile: Pick<Profile, 'role' | 'is_admin' | 'helm_roles'> | null | undefined,
): HelmAccess {
  const roles = profile?.helm_roles ?? []
  const admin = Boolean(profile?.is_admin || profile?.role === 'admin')
  return {
    admin,
    customer: isCustomerLogin(profile),
    warehouseOnly:
      !admin && roles.includes('warehouse') && roles.every((role) => role === 'warehouse'),
    seesWarehouse: admin || roles.includes('warehouse'),
  }
}
